import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../../services/apiClient';
import styles from './UserVerify.module.css';

interface VerificationResponse {
  status: string;
  message: string;
  eventId?: string;
  data?: {
    verification_code: string;
    email: string;
    firstname: string;
    lastname: string;
    submit: string;
  };
}

export const UserVerify: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [verificationState, setVerificationState] = useState<'init' | 'verifying' | 'success' | 'error'>('init');

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');

    if (!token) {
      setError('No token provided.');
      setLoading(false);
      return;
    }

    const verifyToken = async () => {
      try {
        setVerificationState('verifying');
        
        const response = await apiClient.post<VerificationResponse>('/api/v2/web/verify/', { token });

        if (response.data.status === 'passed') {
          setVerificationState('success');
          
          // Kurze Pause für UX
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Erfolgreiche Verifikation - Weiterleitung vorbereiten
          const redirectBase = response.data.eventId 
            ? `/register/?event=${response.data.eventId}` 
            : '/events/';

          if (response.data.data?.verification_code && response.data.data?.email) {
            // Mit allen Parametern weiterleiten
            const params = new URLSearchParams({
              verification_code: response.data.data.verification_code,
              email: response.data.data.email,
              firstname: response.data.data.firstname,
              lastname: response.data.data.lastname,
              submit: response.data.data.submit
            });
            navigate(`${redirectBase}&${params.toString()}`, { replace: true });
          } else {
            // Einfache Weiterleitung
            navigate(redirectBase, { replace: true });
          }
        } else {
          setVerificationState('error');
          setError(response.data.message || 'Verification failed');
          setLoading(false);
        }
      } catch (error: any) {
        console.error('Verification error:', error);
        setVerificationState('error');
        setError('An unexpected error occurred. Please try again.');
        setLoading(false);
      }
    };

    verifyToken();
  }, [location.search, navigate]);

  return (
    <div className={styles.verificationContainer}>
      <i 
        className={`bi ${verificationState === 'error' ? 'bi-exclamation-triangle' : 'bi-shield-lock'} ${verificationState === 'error' ? styles.statusIconError : styles.statusIcon}`}
      ></i>
      
      {loading ? (
        <>
          {verificationState === 'verifying' && (
            <>
              <h3>
                Verifying your account{' '}
                <span className={styles.loadingDots}>
                  <LoadingDots />
                </span>
              </h3>
              <p>Please wait while we check your verification token.</p>
            </>
          )}
          {verificationState === 'success' && (
            <>
              <h3 className={styles.successMessage}>Verification Successful!</h3>
              <p>Redirecting you to the application...</p>
            </>
          )}
          {verificationState === 'init' && (
            <>
              <h3>
                Preparing verification{' '}
                <span className={styles.loadingDots}>
                  <LoadingDots />
                </span>
              </h3>
              <p>Please wait...</p>
            </>
          )}
        </>
      ) : (
        <>
          <h3>Verification Failed</h3>
          <div className={styles.errorMessage}>
            {error}
          </div>
          <button 
            className={`btn btn-primary ${styles.backButton}`}
            onClick={() => navigate('/login', { replace: true })}
          >
            Back to Login
          </button>
        </>
      )}
    </div>
  );
};

// Komponente für die animierten Punkte
const LoadingDots: React.FC = () => {
  return (
    <span className={styles.loadingDots}>
      <span>.</span>
      <span>.</span>
      <span>.</span>
    </span>
  );
};

export default UserVerify;
